'use client';

import UiIcon from '@/components/ui/UiIcon';
import { Children, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import SiteMenu from '@/components/SiteMenu';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ProjectModeSwitch from '@/components/projects/ProjectModeSwitch';
import { getToastStyles, useToast } from '@/components/ui/Toast';
import { apiClient } from '@/lib/api';
import { getAccessToken, getStoredUserRaw, setStoredUser } from '@/lib/authStorage';
import { IconArrowLeft } from '@/components/ui/MonoIcons';

// Комментарий на русском: FPS-счётчик загружается динамически только для админов.
const FpsCounter = dynamic(() => import('@/components/admin/FpsCounter'), { ssr: false });
const HeaderSiteThemeControl = dynamic(() => import('@/components/ui/SiteThemeControl').then((module) => module.HeaderSiteThemeControl), { ssr: false });

type Props = {
  /** Контент слева (рядом с меню). */
  left?: ReactNode;
  /** Контент по центру (например: пользователь на мобилках главной). */
  center?: ReactNode;
  /** Только логотип по центру на страницах личного и публичного профиля. */
  profileBrand?: boolean;
  /** На главной: вход и регистрация для гостей, карточка профиля для пользователей. */
  homeAuth?: boolean;
  /** Фон страницы продолжается под шапкой; безопасный отступ задаёт сама страница. */
  overlay?: boolean;
  /** Контент справа (кнопки/ссылки). */
  children?: ReactNode;
  /** Комментарий на русском: слот для машины времени (таймлайн в редакторе). */
  timeMachineSlot?: ReactNode;
  /** Комментарий на русском: показывать переключатель режимов проекта. */
  showProjectModeSwitch?: boolean;
  /** Комментарий на русском: кастомная логика кнопки "Назад". */
  onBack?: () => boolean | void;
  /** Комментарий на русском: блокирует кнопку, когда внутренней истории раздела нет. */
  backDisabled?: boolean;
  /** Комментарий на русском: скрывает кнопку "Назад" на служебных страницах, которые визуально имитируют главную. */
  hideBackButton?: boolean;
  /** Убирает только нижний мобильный дубль, сохраняя кнопку в шапке. */
  hideFloatingBackButton?: boolean;
  /** Показывает дополнительные действия в меню на мобильных. */
  mobileActions?: boolean;
  /** Совместимость с рабочими экранами: общая шапка теперь всегда компактная. */
  compact?: boolean;
};

type StoredUser = {
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  username?: string | null;
  avatar_display_url?: string | null;
  avatar_url?: string | null;
  avatar?: string | null;
  is_superuser?: boolean;
  is_staff?: boolean;
  role?: { name?: string | null } | null;
  preferences?: Record<string, any>;
};

type SiteGamePresence = {
  game_id: string;
  game_title: string;
  label: string;
  detail?: string | null;
  url?: string | null;
};

export default function SiteHeader({
  left,
  center,
  profileBrand = false,
  homeAuth = false,
  overlay = false,
  children,
  timeMachineSlot,
  showProjectModeSwitch = true,
  onBack,
  backDisabled = false,
  hideBackButton = false,
  hideFloatingBackButton = false,
  mobileActions = false,
}: Props) {
  const pathname = usePathname();
  const router = useRouter();
  const toast = useToast();
  // Фиксируем шапку вверху на всех страницах, кроме мессенджера (там свой скролл/UX).
  const isMessenger = !!pathname?.startsWith('/messages');
  const isProjects = !!pathname?.startsWith('/projects');
  const isHome = pathname === '/';
  const isProfilePage = pathname === '/profile';
  const isFixed = !isMessenger;
  const navRef = useRef<HTMLElement | null>(null);
  const [h, setH] = useState(0);
  const active = toast.active;
  const activeStyles = active ? getToastStyles(active.type) : null;
  const [storedUser, setStoredUserState] = useState<StoredUser | null>(null);
  const [gamePresence, setGamePresence] = useState<SiteGamePresence | null>(null);
  const [compactNavigation, setCompactNavigation] = useState(true);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 1279px)');
    const update = () => setCompactNavigation(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let revision = 0;
    const readUser = () => {
      const currentRevision = ++revision;
      const token = homeAuth ? getAccessToken() : null;
      let user: StoredUser | null = null;
      try {
        const raw = getStoredUserRaw();
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) user = parsed;
      } catch {
        // Повреждённый кэш не подменяет состояние сессии.
      }
      if (homeAuth && !token) {
        setStoredUserState(null);
        return;
      }
      setStoredUserState(user ?? (token ? {} : null));
      if (token && !user) {
        void apiClient.getCurrentUser().then((fresh) => {
          if (revision !== currentRevision) return;
          const activeToken = getAccessToken();
          if (!activeToken) return;
          // Тихое обновление токена не отправляет auth-событие: перечитываем профиль уже с новым токеном.
          if (activeToken !== token) readUser();
          else setStoredUser(fresh);
        }).catch(() => {
          // API сам завершает недействительную сессию; при сетевой ошибке оставляем нейтральную карточку.
        });
      }
    };

    readUser();
    const onAuth = () => readUser();
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === 'access' || event.key === 'user') readUser();
    };
    window.addEventListener('lisad_auth_changed', onAuth);
    window.addEventListener('storage', onStorage);
    return () => {
      revision++;
      window.removeEventListener('lisad_auth_changed', onAuth);
      window.removeEventListener('storage', onStorage);
    };
  }, [homeAuth]);

  useEffect(() => {
    if (!storedUser) {
      setGamePresence(null);
      return;
    }

    let mounted = true;
    const loadPresence = async () => {
      try {
        const response = await apiClient.request<{ game_presence?: SiteGamePresence | null }>('/api/chat/status/game/');
        if (mounted) setGamePresence(response.game_presence ?? null);
      } catch {
        if (mounted) setGamePresence(null);
      }
    };

    void loadPresence();
    const timer = window.setInterval(() => void loadPresence(), 25000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [storedUser]);

  const legacyCenterNode =
    Children.count(center) > 0 ? (
      center
    ) : (
      <Link
        prefetch={false}
        href="/"
        className="text-2xl sm:text-3xl font-bold text-site-text leading-none whitespace-nowrap"
        aria-label="На главную"
      >
        ЛИСАД
      </Link>
    );

  // Данные пользователя для десктопной шапки.
  const userDisplayName = storedUser
    ? (`${storedUser.first_name || ''} ${storedUser.last_name || ''}`.trim() ||
        storedUser.display_name ||
        storedUser.username ||
        'Пользователь')
    : 'ГОСТЬ ЛИСАД';
  // В компактной карточке показываем только первое имя, даже если заполнено ФИО.
  const userDisplayShort = userDisplayName.trim().split(/\s+/)[0] || userDisplayName;
  const userRoleName = storedUser ? (storedUser.is_superuser ? 'Суперадмин' : storedUser.role?.name || 'Пользователь') : '';
  const userAvatarSrc = storedUser
    ? storedUser.avatar_display_url || storedUser.avatar_url || storedUser.avatar || '/uploads/chat_room_avatars/lisad_default_avatar.gif'
    : '/uploads/branding/mail-logo.png';
  const userInitial = storedUser ? (storedUser.first_name?.[0] || storedUser.username?.[0] || '?').toUpperCase() : '?';
  const roleName = (storedUser?.role?.name || '') as string;
  const isAdminish = Boolean(storedUser?.is_superuser || storedUser?.is_staff || /админ|модератор|сотрудник/i.test(roleName));
  const fpsCounterEnabled = Boolean((storedUser as any)?.preferences?.fps_counter_enabled);
  const profileAccessRingColor = storedUser ? (isAdminish ? '#F59E0B' : '#22C55E') : '#22C55E';
  const profileHref = storedUser ? (isProfilePage ? '/' : '/profile') : '/';
  const profileTitle = storedUser ? (isProfilePage ? 'На главную' : 'Профиль') : 'На главную';
  const gamePresenceText = gamePresence ? `Играет: ${gamePresence.game_title || 'игра'}` : '';
  const hasMobileActions = mobileActions && Children.count(children) > 0;

  const profileCenterNode = (
    <div className="flex items-center justify-center">
      {/* На мобильных карточка сжимается до аватара, оставаясь по центру. */}
      <div className="flex items-center relative">
        {/* Основная кнопка профиля */}
        <Link
          prefetch={false}
          href={profileHref}
          className="group relative h-8 w-8 min-[761px]:h-12 min-[761px]:w-auto overflow-visible inline-flex items-center justify-center min-[761px]:gap-2 rounded-full border border-site-border bg-site-surface/35 min-[761px]:pl-1 min-[761px]:pr-3 py-0 shadow-[0_12px_30px_rgba(0,0,0,0.10)] ring-1 ring-site-warning/10 transition hover:bg-site-surface/55 hover:border-site-warning/40 hover:ring-2 hover:ring-site-warning/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-site-warning/45 active:scale-[0.99]"
          aria-label={profileTitle}
          title={profileTitle}
        >
          <span
            key={userAvatarSrc}
            className={`relative inline-flex shrink-0 h-7 w-7 min-[761px]:h-10 min-[761px]:w-10 items-center justify-center rounded-full overflow-hidden bg-site-raised/40 border-2 shadow-[0_10px_25px_rgba(0,0,0,0.10)] ring-0 ring-site-warning/40 transition-[box-shadow] group-hover:ring-2`}
            style={{ borderColor: profileAccessRingColor }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            {userAvatarSrc ? (
              <img
                src={userAvatarSrc}
                alt={`Аватар: ${userDisplayName}`}
                className="w-full h-full object-cover"
                loading="eager"
                decoding="async"
                onError={(e) => {
                  const img = e.currentTarget;
                  const fallback = img.nextElementSibling as HTMLElement | null;
                  img.style.display = 'none';
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <span
              className={
                userAvatarSrc
                  ? 'hidden w-full h-full items-center justify-center text-base font-bold text-site-text'
                  : 'flex w-full h-full items-center justify-center text-base font-bold text-site-text'
              }
            >
              {userInitial}
            </span>
          </span>
          <span className="hidden min-[761px]:flex flex-col items-start leading-tight">
            <span className="text-sm font-semibold text-site-text max-w-[110px] lg:max-w-[180px] truncate transition-colors group-hover:text-site-warning">
              {userDisplayShort}
            </span>
            {gamePresenceText ? (
              <span className="text-xs font-semibold text-site-warning truncate max-w-[110px] lg:max-w-[180px]" title={gamePresence?.detail ?? gamePresenceText}>
                {gamePresenceText}
              </span>
            ) : userRoleName ? (
              <span className="text-xs text-site-muted truncate max-w-[110px] lg:max-w-[180px]">{userRoleName}</span>
            ) : null}
          </span>
        </Link>
      </div>
    </div>
  );

  const showBackButton = !hideBackButton && !isHome && !isMessenger;

  const brandNode = !isMessenger ? (
    <Link
      prefetch={false}
      href="/"
      className={`${profileBrand || (!isProjects && compactNavigation) ? 'inline-flex h-8 w-24 sm:h-10 sm:w-28' : `${isProjects ? 'hidden 2xl:inline-flex' : 'hidden xl:inline-flex'} h-10 w-[140px]`} shrink-0 items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/60`}
      aria-label="На главную"
      title="На главную"
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/assets/branding/lisad-wordmark.png"
        alt=""
        className="h-full w-full object-contain"
        style={{ filter: 'drop-shadow(0 1px 1px color-mix(in srgb, var(--site-logo-plate) 70%, transparent)) drop-shadow(0 0 5px color-mix(in srgb, var(--site-logo-plate) 32%, transparent))' }}
        loading="lazy"
        decoding="async"
      />
    </Link>
  ) : null;

  const guestAuthNode = (
    <div className="flex items-center justify-center gap-1.5 sm:gap-2" aria-label="Авторизация">
      <Link prefetch={false} href="/login" className="inline-flex min-h-8 min-[761px]:min-h-11 items-center justify-center rounded-xl min-[761px]:rounded-full border border-site-accent/50 px-3 sm:px-4 text-xs sm:text-sm font-medium whitespace-nowrap text-site-accent transition-colors hover:bg-site-accent/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/60">Войти</Link>
      <Link prefetch={false} href="/register" className="inline-flex min-h-8 min-[761px]:min-h-11 items-center justify-center rounded-xl min-[761px]:rounded-full border border-transparent bg-site-action px-3 sm:px-4 text-xs sm:text-sm font-medium whitespace-nowrap text-site-on-accent transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/60">Регистрация</Link>
    </div>
  );

  const hasCenterBrand = profileBrand || (!homeAuth && !isProjects && !isMessenger && (compactNavigation || !storedUser));

  // В проектах центр остаётся под таймлайн; профильные страницы показывают только бренд.
  const centerNode = homeAuth ? (storedUser ? profileCenterNode : guestAuthNode) : profileBrand ? brandNode : isMessenger ? legacyCenterNode : isProjects ? legacyCenterNode : compactNavigation || !storedUser ? brandNode : profileCenterNode;

  const backNode = showBackButton ? (
    <Button
      variant="ghost"
      size="sm"
      className={`${isProjects && !homeAuth ? 'inline-flex' : 'hidden sm:inline-flex'} shrink-0 rounded-full border border-site-warning/35 bg-site-bg/15 hover:bg-site-surface/35 hover:border-site-warning/55 disabled:cursor-not-allowed disabled:opacity-40`}
      disabled={backDisabled}
      onClick={() => {
        const handled = onBack?.();
        if (handled) return;
        try {
          if (typeof window !== 'undefined' && window.history.length > 1) router.back();
          else router.push('/');
        } catch {
          router.push('/');
        }
      }}
      title={isProjects ? 'Назад по проекту' : 'Назад'}
      aria-label={isProjects ? 'Назад по проекту' : 'Назад'}
    >
      <IconArrowLeft className="w-4 h-4" />
      <span className="hidden lg:inline">Назад</span>
    </Button>
  ) : null;

  // Вторичные действия не расширяют строку: на узких экранах их единственный экземпляр находится в меню.
  const secondaryActions = (
    <>
      <div className={isProjects || hasMobileActions ? 'contents' : 'hidden sm:contents'}>
        {isAdminish && fpsCounterEnabled ? <FpsCounter /> : null}
        {children}
      </div>
      {isProjects && showProjectModeSwitch ? <ProjectModeSwitch inMenu={compactNavigation} /> : null}
    </>
  );
  const menuNode = (
    <SiteMenu compact align="start" actions={compactNavigation && (children || (isAdminish && fpsCounterEnabled) || (isProjects && showProjectModeSwitch)) ? (
      <div className="flex min-w-0 flex-col gap-2">{secondaryActions}</div>
    ) : undefined} />
  );
  const rightNode = (
    <>
      <HeaderSiteThemeControl />
      {!compactNavigation ? secondaryActions : null}
    </>
  );

  useLayoutEffect(() => {
    if (!isFixed) return;
    const el = navRef.current;
    if (!el) return;

    const update = () => {
      const hh = el.offsetHeight;
      setH(hh);
      // Комментарий на русском: сохраняем высоту шапки в CSS-переменную, чтобы подгонять высоту рабочих областей.
      try {
        document.documentElement.style.setProperty('--lisad-header-h', `${hh}px`);
      } catch {
        // ignore
      }
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isFixed]);

  return (
    <>
      <nav
        ref={navRef}
        className={`${isFixed ? 'fixed top-0 left-0 right-0' : 'relative'} z-50 ${homeAuth ? 'p-1 min-[761px]:p-3' : 'p-2 sm:p-3'}`}
      >
        {/* Ограничиваем максимальную ширину шапки до 1900px */}
        <div className="max-w-[1900px] mx-auto">
        <Card
          variant="glass"
          useWidgetSettings={true}
          style={{ backgroundColor: 'var(--site-header)' }}
          className={`${homeAuth ? 'px-2 py-1 min-[761px]:px-3 min-[761px]:py-2' : 'px-2 sm:px-3 py-1.5 sm:py-2'} border !border-site-border-control/40 overflow-visible`}
        >
          {isProjects && !homeAuth ? (
            <div data-site-header-row="project" className="relative flex items-center gap-2">
              <div data-site-header-left className="flex min-w-0 shrink-0 items-center gap-1 lg:gap-2">
                {brandNode}
                {backNode}
                {menuNode}
                <div className="hidden xl:contents">{left}</div>
              </div>
              <div data-site-header-center className="min-w-0 flex-1">
                {timeMachineSlot || centerNode}
              </div>
              <div data-site-header-right className="flex shrink-0 items-center gap-2">
                {rightNode}
              </div>
            </div>
          ) : (
            <>
              {/* Равные боковые колонки удерживают профиль в центре без второго ряда. */}
              <div data-site-header-row className="relative grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-2">
                <div data-site-header-left className="col-start-1 row-start-1 flex min-w-0 items-center gap-1 sm:gap-2">
                  {!hasCenterBrand && !compactNavigation && brandNode}
                  {backNode}
                  {menuNode}
                  <div className="hidden xl:contents">{left}</div>
                </div>

                <div data-site-header-center className="relative z-10 col-start-2 row-start-1 flex min-w-0 items-center justify-center">
                  {/* Комментарий на русском: по центру либо основной контент (ЛИСАД/профиль), либо активное уведомление (без сдвига верстки). */}
                  <div className={`grid ${homeAuth ? 'h-8 min-[761px]:h-12' : 'h-10 sm:h-12'} max-w-[680px] overflow-visible place-items-center`}>
                    <div
                      className={`col-start-1 row-start-1 flex items-center justify-center transition-all duration-200 ${
                        active ? '-translate-y-3 opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
                      }`}
                    >
                      {centerNode}
                    </div>

                    <div
                      className={`col-start-1 row-start-1 flex items-center justify-center transition-all duration-200 ${
                        active ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0 pointer-events-none'
                      }`}
                      role="status"
                      aria-live="polite"
                    >
                      {active ? (
                        <div
                          // Комментарий на русском: делаем карточку уведомления выше примерно на 30%, но без влияния на высоту шапки (она лежит поверх).
                          className={`group relative z-20 h-[130%] w-fit max-w-full rounded-xl border sm:max-w-[680px] ${activeStyles?.border || 'border-site-border-control/40'} ${
                            activeStyles?.bg || 'bg-site-bg/70'
                          } backdrop-blur-md shadow-xl px-3 py-2 flex items-center gap-2 cursor-pointer select-none`}
                          title={active.href ? 'Открыть' : undefined}
                          onClick={() => {
                            if (!active.href) return;
                            router.push(active.href);
                          }}
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          {active.iconUrl ? (
                            <img
                              src={active.iconUrl}
                              alt=""
                              className="w-7 h-7 rounded-full object-cover border border-site-border flex-shrink-0"
                            />
                          ) : (
                            <div className="text-lg leading-none flex-shrink-0">{<UiIcon name={activeStyles?.icon || 'ℹ️'} />}</div>
                          )}

                          <div className="min-w-0">
                            {active.title ? (
                              <div className={`text-sm font-semibold ${activeStyles?.title || 'text-site-text'} truncate`}>{active.title}</div>
                            ) : null}
                            <div className={`text-xs sm:text-sm ${activeStyles?.text || 'text-site-text/90'} truncate`}>{active.message}</div>
                          </div>

                          <button
                            type="button"
                            className="ml-1 text-site-secondary/70 hover:text-site-text transition-colors leading-none text-xl flex-shrink-0"
                            aria-label="Закрыть уведомление"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toast.dismiss(active.id);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div
                  data-site-header-right
                  className="col-start-3 row-start-1 flex min-w-0 items-center justify-end gap-2"
                >
                  {rightNode}
                </div>
              </div>

              {/* Комментарий на русском: машина времени (таймлайн) в редакторе проектов */}
              {timeMachineSlot && (
                <div className="mt-4 pt-4 border-t border-site-border">
                  {timeMachineSlot}
                </div>
              )}
            </>
          )}

        </Card>
        </div>
      </nav>
      {isFixed && !overlay ? <div aria-hidden style={{ height: h }} /> : null}
      {/* Комментарий на русском: мобилка — плавающая стрелка "Назад" внизу слева, на одном уровне с FAB мессенджера. */}
      {showBackButton && !hideFloatingBackButton ? (
        <div className="fixed bottom-6 left-6 z-40 sm:hidden">
          <button
            type="button"
            onClick={() => {
              // Комментарий на русском: назад по истории, а если истории нет — на главную.
              try {
                if (typeof window !== 'undefined' && window.history.length > 1) router.back();
                else router.push('/');
              } catch {
                router.push('/');
              }
            }}
            aria-label="Назад"
            title="Назад"
            className="w-14 h-14 rounded-full backdrop-blur-xl border transition-all duration-200 active:scale-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-site-accent/40"
            style={{
              background: 'linear-gradient(145deg, var(--site-surface), var(--site-inset))',
              borderColor: 'var(--site-border-control)',
            }}
          >
            <span aria-hidden className="text-xl leading-none text-site-text">
              ←
            </span>
          </button>
        </div>
      ) : null}
    </>
  );
}
